// Phase 6 — Sales Report screen, migrated to shadcn/Tailwind.
//
// UI only: the MUI Table, antd Pagination/Menu/Select and the antd DatePicker /
// RangePicker are replaced by the shared DataTable (ReportsTable), a StatCard
// total, a shadcn Select month/year filter and a Popover+Calendar date-range
// picker (DateRangePicker). Every dispatch / useSelector / payload is unchanged —
// the filter handlers below mutate `filterData` exactly as the legacy did, so
// getReportListAction receives byte-identical payloads.
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Filter, FilterX } from "lucide-react";
import { DateRange } from "react-day-picker";
import { getReportListAction } from "../../Store/actions/apparelAction";
import ApparelMenu from "../SubComponents/apparelMenu";
import { deepCopyFunction } from "../../Utils/helpers";
import AppConstant from "../../Globals/AppConstants";
import { PageHeader } from "../../shared/page-header";
import { StatCard } from "../../shared/stat-card";
import { Button } from "ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import ReportsTable from "./ReportsTable";
import { DateRangePicker } from "./DateRangePicker";

const searchtemp: any = {
    searchText: "",
    offset: 0,
    limit: 10,
    month: '',
    year: '',
    fromDate: '',
    toDate: ''
};

const months = [
    { value: 0, label: 'All' },
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

// Year options: 2020 → current year, latest first.
const years: number[] = (() => {
    const current = moment().year();
    const list: number[] = [];
    for (let y = current; y >= 2020; y--) list.push(y);
    return list;
})();

const ReportListScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApparelReducerState = useSelector((state: any) => state.ApparelReducerState);
    const { getReportListData, getReportListOnLoad } = ApparelReducerState;
    const getReportList = getReportListData?.getReportList?.orderData;
    const getReprtPage = getReportListData?.getReportList?.page;
    const totalPage = getReprtPage?.totalCount;
    const currentPage = getReprtPage?.currentPage;

    const [filterData, setFilterData] = useState<any>(deepCopyFunction(searchtemp));
    const [filterImage, setFilterImage] = useState(false);
    // Replaces the antd Form 'rangepicker' field value (used for display + clearing).
    const [rangeValue, setRangeValue] = useState<DateRange | undefined>(undefined);

    const reportListApi = () => {
        let payload = {
            month: filterData?.month == 0 ? '' : filterData?.month,
            year: filterData?.year == null ? '' : filterData?.year,
            fromDate: filterData?.fromDate,
            toDate: filterData?.toDate,
            paging: {
                limit: filterData?.limit,
                offset: filterData?.offset
            }
        }
        dispatch(getReportListAction(payload))
    }

    function getFirstDayOfMonth(monthNumber: any, year: any) {
        setRangeValue(undefined);
        if (monthNumber != 0) {
            const date = moment({ year: year, month: monthNumber - 1 });

            setFilterData({
                ...filterData,
                month: date.format('YYYY-MM-01')
            })
        }
        else {
            setFilterData({
                ...filterData,
                month: 0
            })
        }
    }

    useEffect(() => {
        if (filterData?.month != "" || filterData?.year != "" || (filterData?.fromDate != "" && filterData?.toDate != ""))
            reportListApi()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterData])

    useEffect(() => {
        setFilterData({
            ...filterData,
            year: moment().format('YYYY-MM-DD'),
            month: moment().format('YYYY-MM-DD')
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // DataTable gives a 1-based page; reuse the legacy offset math.
    const onPageChange = (page: any) => {
        let offset = ((page * 10) - 10 == 0) ? 0 : (page * 10) - 10;
        setFilterData({ ...filterData, offset: offset, page: page })
    }

    const datePickerChange = (year: any) => {
        setRangeValue(undefined);
        setFilterData({
            ...filterData,
            year: year != null ? moment().year(Number(year)).format('YYYY-MM-DD') : null
        })
    }

    const RangePickerChange = (range: DateRange | undefined) => {
        setRangeValue(range);
        if (range && range.from && range.to) {
            setFilterData({
                ...filterData,
                fromDate: moment(range.from).format('YYYY-MM-DD'),
                toDate: moment(range.to).format('YYYY-MM-DD'),
                month: '',
                year: ''
            })
        }
        else if (!range) {
            setFilterData({
                ...filterData,
                fromDate: '',
                toDate: '',
                month: moment().format('YYYY-MM-DD'),
                year: moment().format('YYYY-MM-DD')
            })
        }
    }

    // Month/year controls are disabled while a date range is active (or the range
    // filter is toggled on) — same rule the legacy used.
    const controlsDisabled = filterImage == false ? (filterData?.fromDate && filterData?.toDate ? true : false) : true;

    const monthNum = filterImage == true ? AppConstant?.zero
        : filterData?.month != 0 ? (filterData?.month ? parseInt(moment(filterData?.month).format('M')) : parseInt(moment().format('M'))) : AppConstant.zero;

    const yearShown = filterImage == true ? null
        : filterData?.fromDate && filterData?.toDate ? null
            : (filterData?.year == null ? null : filterData?.year ? moment(filterData?.year) : moment());
    const yearValue = yearShown ? yearShown.format('YYYY') : undefined;

    const filterToolbar = (
        <div className="flex flex-wrap items-center gap-2">
            <Button
                variant={filterImage ? "secondary" : "outline"}
                size="icon"
                aria-label={filterImage ? "Hide date range" : "Filter by date range"}
                onClick={() => setFilterImage(!filterImage)}
            >
                {filterImage ? <FilterX className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
            </Button>

            {filterImage && (
                <DateRangePicker value={rangeValue} onChange={RangePickerChange} disableFuture />
            )}

            <Select
                value={String(monthNum)}
                disabled={controlsDisabled}
                onValueChange={(v) => getFirstDayOfMonth(Number(v), filterData?.year)}
            >
                <SelectTrigger className="w-[130px]"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                    {months.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select
                value={yearValue}
                disabled={controlsDisabled}
                onValueChange={(v) => datePickerChange(v)}
            >
                <SelectTrigger className="w-[110px]"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                    {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="4" />
            {/* <PageHeader title={AppConstant?.menus?.report} /> */}

            {/* Sales / Purchase report tabs (Purchase is a placeholder, as in the legacy). */}
            <div className="mb-4 flex items-center gap-1 border-b">
                <button
                    type="button"
                    onClick={() => navigate("/report/list")}
                    className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                >
                    {AppConstant.SALES_REPORT}
                </button>
                <button
                    type="button"
                    className="border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                    {AppConstant.PURCHASE_REPORT}
                </button>
            </div>

            {getReportListData && (
                <div className="mb-4 grid grid-cols-1 sm:max-w-xs">
                    <StatCard label="Total Amount" value={getReportListData?.getReportList?.totalAmount} />
                </div>
            )}

            <ReportsTable
                data={getReportList}
                loading={getReportListOnLoad}
                toolbar={filterToolbar}
                pagination={{
                    page: currentPage || 1,
                    total: totalPage || 0,
                    onChange: onPageChange,
                }}
            />
        </div>
    )
}

export default ReportListScreen;
